/**
 * Network Monitor Module (PRD 04)
 *
 * Observes TCP/UDP connections via conntrack / ss / /proc/net.
 * Detects port scans and SYN-flood-style patterns.
 * Emits detections through the event bus for the rule engine.
 */

import { spawnSync } from 'node:child_process';
import { existsSync } from 'node:fs';
import type { EventBus } from '../../daemon/event-bus.js';
import { insertEvent } from '../../core/state.js';
import type { ThreatEvent, EventSeverity } from '../../types/events.js';

interface ConnectionRecord {
  source_ip: string;
  dest_port: number;
  timestamp: number;
}

interface ScanTracker {
  ports: Set<number>;
  firstSeen: number;
  lastSeen: number;
  count: number;
}

export class NetworkMonitor {
  private active = false;
  private pollTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private scanTrackers = new Map<string, ScanTracker>();
  private halfOpenTrackers = new Map<string, { count: number; firstSeen: number }>();
  private lastConnections = new Set<string>();

  // Proof-of-life accounting, reset each heartbeat.
  private obsConnections = 0;
  private obsSources = new Set<string>();
  private noticed = new Set<string>();

  // Config
  private pollIntervalMs = 5000;
  private heartbeatMs = 900_000;   // 15 min: proof of life, low volume
  private portScanThreshold = 10;  // unique listening ports -> high (bannable)
  private portScanNoticeThreshold = 5; // 5-9 -> medium notice (never bans)
  private portScanWindowMs = 30_000;
  private synFloodThreshold = 50;  // half-open connections
  private synFloodWindowMs = 10_000;

  constructor(private bus: EventBus) {}

  start(): boolean {
    if (!this.hasConntrackOrSs()) {
      return false;
    }
    this.active = true;
    this.pollTimer = setInterval(() => this.poll(), this.pollIntervalMs);
    // Proof of life: a low-severity summary so "0 detections" reads as
    // "watching, quiet" rather than "dead".
    this.heartbeatTimer = setInterval(() => this.heartbeat(), this.heartbeatMs);
    return true;
  }

  stop(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.pollTimer = null;
    this.heartbeatTimer = null;
    this.active = false;
  }

  private heartbeat(): void {
    const conns = this.obsConnections;
    const srcs = this.obsSources.size;
    this.obsConnections = 0;
    this.obsSources.clear();
    this.noticed.clear();
    const listening = this.listeningPorts().size;
    this.emitEvent(
      'info',
      `network-monitor alive: ${conns} inbound connection(s) from ${srcs} source(s) across ${listening} listening port(s) in the last ${Math.round(this.heartbeatMs / 60000)}m`,
      undefined,
      { heartbeat: true, inbound: conns, sources: srcs, listening_ports: listening },
    );
  }

  isActive(): boolean { return this.active; }

  private hasConntrackOrSs(): boolean {
    const ss = spawnSync('ss', ['--version'], { stdio: 'pipe' });
    if (ss.status === 0) return true;
    // Try /proc/net/tcp
    return existsSync('/proc/net/tcp');
  }

  /**
   * The TCP ports this host actually listens on.
   *
   * Everything else in a socket table is an *outbound* connection, whose local
   * port is an ephemeral one picked per connection. Counting those as "ports
   * probed" is what made every busy upstream look like a port scanner: open ten
   * connections to a registry, a database or an API inside thirty seconds and
   * the peer had "probed 10 ports". With auto-defence enforcing, that banned
   * our own dependencies — and any host we ssh out to.
   */
  private listeningPorts(): Set<number> {
    const ports = new Set<number>();
    try {
      const out = spawnSync('ss', ['-ltnH'], { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'], timeout: 3000 });
      if (out.status === 0 && out.stdout) {
        for (const line of out.stdout.split('\n')) {
          const parts = line.trim().split(/\s+/);
          // State Recv-Q Send-Q Local:Port Peer:Port
          const local = parts[3];
          if (!local) continue;
          const port = Number.parseInt(local.slice(local.lastIndexOf(':') + 1), 10);
          if (Number.isFinite(port)) ports.add(port);
        }
      }
    } catch { /* no ss: fall back to counting nothing, which is the safe side */ }
    return ports;
  }

  private poll(): void {
    try {
      const connections = this.getConnections();
      this.obsConnections += connections.length;
      for (const c of connections) this.obsSources.add(c.source_ip);
      this.analyzePortScans(connections);
      this.analyzeSynFlood(connections);
      this.cleanupTrackers();
    } catch {
      // Graceful degradation
    }
  }

  private getConnections(): ConnectionRecord[] {
    const records: ConnectionRecord[] = [];
    const now = Date.now();

    // Only traffic arriving at a port we serve can be a scan of us. Without a
    // listener list we record nothing rather than guess: a missed detection is
    // recoverable, a banned upstream is an outage.
    const listening = this.listeningPorts();
    if (listening.size === 0) return records;

    try {
      // Try conntrack first
      const ct = spawnSync('conntrack', ['-L', '-p', 'tcp', '-o', 'extended'], {
        encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'], timeout: 3000,
      });
      if (ct.status === 0 && ct.stdout) {
        for (const line of ct.stdout.split('\n')) {
          const srcMatch = line.match(/src=(\d+\.\d+\.\d+\.\d+)/);
          const dportMatch = line.match(/dport=(\d+)/);
          if (srcMatch && dportMatch) {
            const port = parseInt(dportMatch[1]);
            // Inbound only: an entry whose destination is not a port we serve
            // is a connection *we* opened, and its peer is not probing us.
            if (!listening.has(port)) continue;
            records.push({ source_ip: srcMatch[1], dest_port: port, timestamp: now });
          }
        }
        if (records.length > 0) return records;
      }
    } catch { /* fallthrough */ }

    try {
      // Fallback to ss
      const ss = spawnSync('ss', ['-tnp', '-H'], {
        encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'], timeout: 3000,
      });
      if (ss.status === 0 && ss.stdout) {
        for (const line of ss.stdout.split('\n')) {
          // ss output: State  Recv-Q  Send-Q  Local:Port  Peer:Port  Process
          const parts = line.trim().split(/\s+/);
          if (parts.length < 5) continue;
          const peerParts = parts[4].split(':');
          const localParts = parts[3].split(':');
          if (peerParts.length >= 2 && localParts.length >= 2) {
            const sourceIp = peerParts.slice(0, -1).join(':');
            const destPort = parseInt(localParts[localParts.length - 1]);
            // `destPort` here is our *local* port. It only means "the port
            // this peer reached us on" when we are the listener; otherwise it
            // is the ephemeral port we chose for an outbound connection.
            if (
              sourceIp &&
              !isNaN(destPort) &&
              listening.has(destPort) &&
              !this.isLocalIp(sourceIp)
            ) {
              records.push({ source_ip: sourceIp, dest_port: destPort, timestamp: now });
            }
          }
        }
      }
    } catch { /* fallthrough */ }

    return records;
  }

  private analyzePortScans(connections: ConnectionRecord[]): void {
    const now = Date.now();

    for (const conn of connections) {
      const key = conn.source_ip;
      let tracker = this.scanTrackers.get(key);

      if (!tracker) {
        tracker = { ports: new Set(), firstSeen: now, lastSeen: now, count: 0 };
        this.scanTrackers.set(key, tracker);
      }

      tracker.ports.add(conn.dest_port);
      tracker.lastSeen = now;
      tracker.count++;

      const withinWindow = (now - tracker.firstSeen) <= this.portScanWindowMs;

      // Check threshold within window
      if (tracker.ports.size >= this.portScanThreshold && withinWindow) {
        this.emitEvent(
          'high',
          `Port scan detected: ${conn.source_ip} probed ${tracker.ports.size} ports in ${Math.round((now - tracker.firstSeen) / 1000)}s`,
          conn.source_ip,
          { ports_scanned: tracker.ports.size, window_seconds: Math.round((now - tracker.firstSeen) / 1000) },
        );
        // Reset tracker after alert
        this.scanTrackers.delete(key);
      } else if (
        // A smaller spread across our listening ports. The event itself is
        // `medium`, but the `port-scan-indicator` rule matches it and bans on
        // first sight. These are inbound-to-listening only, so the ephemeral
        // outbound-port bug that once banned our upstreams cannot recur here.
        tracker.ports.size >= this.portScanNoticeThreshold &&
        withinWindow &&
        !this.noticed.has(key)
      ) {
        this.noticed.add(key);
        this.emitEvent(
          'medium',
          `Possible port scan: ${conn.source_ip} touched ${tracker.ports.size} listening ports in ${Math.round((now - tracker.firstSeen) / 1000)}s`,
          conn.source_ip,
          { ports_scanned: tracker.ports.size, window_seconds: Math.round((now - tracker.firstSeen) / 1000), notice: true },
        );
      }
    }
  }

  private analyzeSynFlood(connections: ConnectionRecord[]): void {
    // Count SYN_RECV (half-open) states via ss
    try {
      const ss = spawnSync('ss', ['-tn', 'state', 'syn-recv', '-H'], {
        encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'], timeout: 3000,
      });
      if (ss.status !== 0 || !ss.stdout) return;

      const perSource = new Map<string, number>();

      for (const line of ss.stdout.split('\n')) {
        const parts = line.trim().split(/\s+/);
        if (parts.length < 5) continue;
        const peer = parts[4].split(':');
        const ip = peer.slice(0, -1).join(':');
        if (ip) perSource.set(ip, (perSource.get(ip) || 0) + 1);
      }

      for (const [ip, count] of perSource) {
        if (count >= this.synFloodThreshold) {
          this.emitEvent(
            'critical',
            `SYN flood indicators: ${count} half-open connections from ${ip}`,
            ip,
            { half_open_count: count },
          );
        }
      }
    } catch { /* graceful */ }
  }

  private emitEvent(severity: EventSeverity, message: string, sourceIp?: string, details?: Record<string, unknown>): void {
    const event: ThreatEvent = {
      timestamp: new Date(),
      module: 'network-monitor',
      category: 'network',
      severity,
      message,
      source_ip: sourceIp,
      details,
    };
    try { insertEvent(event); } catch { /* db optional */ }
    this.bus.publish(event);
  }

  private cleanupTrackers(): void {
    const now = Date.now();
    for (const [key, tracker] of this.scanTrackers) {
      if (now - tracker.lastSeen > this.portScanWindowMs * 2) {
        this.scanTrackers.delete(key);
      }
    }
  }

  private isLocalIp(ip: string): boolean {
    return ip === '127.0.0.1' || ip === '::1' || ip === '0.0.0.0' || ip.startsWith('::ffff:127.');
  }
}
